import { Account } from '@ankor-io/common/types/account'
import { WebhookSystem } from '@ankor-io/common/types/system'

import { AWXCardholderStatus, AWXCardholderType, AWXIndividual } from './cardholder'

/**
 * AWX authorization scopes
 * @see https://www.airwallex.com/docs/api#/Authentication/Embedded_Components/_api_v1_authentication_authorize/post
 */
export enum AWXAuthorizationScopes {
  /**
   * used by the embedded onboarding component
   */
  ONBOARDING = 'w:awx_action:onboarding',
  /**
   * used by the embedded payout component and the embedded beneficiary component
   */
  TRANSFERS_EDIT = 'w:awx_action:transfers_edit',
  /**
   * used by the embedded KYB component
   */
  KYB = 'w:awx_action:kyb',
  /**
   * used by the embedded RFI component to view open RFIs
   */
  RFI = 'r:awx_action:rfi_view',
  /**
   * used by the embedded RFI component to edit and submit responses to RFIs
   */
  RFI_EDIT = 'w:awx_action:rfi_edit',
  /**
   * used by the embedded SCA component to get the context of SCA
   */
  SCA = 'r:awx_action:sca_view',
  /**
   * used by the embedded SCA component to set up or change the authentication factors of SCA
   */
  SCA_EDIT = 'w:awx_action:sca_edit',
}

export type AwxConnectedAccount = {
  id: string
  name: string
  status: AWXConnectedAccountStatus
  organization_id: string
  awxEntity?: AWXEntity
  identifier?: string
  type: AwxConnectedAccountType
  vesselId?: string
}

export enum AwxConnectedAccountType {
  APA = 'APA',
  OPERATIONS = 'OPERATIONS',
}

export type ConnectedAccountWithVessel = {
  connectedAccount: AwxConnectedAccount
  vessel: { id: string; name: string }
}

export type UserWithConnectedAccounts = {
  user: Account
  connectedAccounts: AwxConnectedAccount[]
}

export type AWXEntity = {
  id: string
  name: string
  organization_id: string
}

export type AWXEntityVessel = {
  vesselId: string
  vesselName: string
  connectedAccounts: {
    connectedAccountId: string
    connectedAccountName: string
  }[]
}

export type AWXEntityWithVesselsAndConnectedAccounts = {
  id: string
  name: string
  vessels: AWXEntityVessel[]
}

/**
 * See statuses here https://www.airwallex.com/docs/api#/Scale/Accounts/_api_v1_accounts_create/post
 *
 * Keep these uppercase as per the docs
 */
export enum AWXConnectedAccountStatus {
  ACTION_REQUIRED = 'ACTION_REQUIRED',
  CREATED = 'CREATED',
  SUBMITTED = 'SUBMITTED',
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
}

export type AWXConfig = {
  clientId: string
  env: string
  domain: string
}

/**
 * Options for verifying the signature of a webhook
 * @see https://www.airwallex.com/docs/developer-tools__listen-for-webhook-events#check-webhook-signatures
 */
export type WebhookVerificationOptions = {
  /**
   * Our tolerance in seconds for how long a webhook is valid
   */
  toleranceInSeconds?: number
}

export type WebhookSignatureParams = {
  /**
   * raw JSON payload of the webhook
   */
  payload: string
  /**
   * the signature of the webhook from the x-signature header
   */
  signature: string
  /**
   * the timestamp of the webhook from the x-timestamp header
   */
  timestamp: string
  /**
   * The webhook secret
   */
  secret: string
}

export type Webhook = {
  /**
   * The id of the webhook.
   */
  id: string
  /**
   * The system this webhook was created for.
   */
  system: WebhookSystem
  /**
   * The environment this webhook was created for.
   */
  env: string
}

/**
 * Represents a webhook registered with AWX
 */
export type AWXWebhook = Webhook & {
  /**
   * The webhook full url. Likely a pay-endpoint url that should handle the webhook payload
   */
  url: string
  /**
   * The secret of the webhook. This is used to verify the signature of the webhook. Provided by AWX on creation.
   */
  secret: string
  /**
   * The request id used for idempotency.
   */
  requestId: string
}

export type WebhookDetailsPayload = {
  id: string
  system: WebhookSystem
  env: string
  url: string
  requestId: string
}

export type WebhookPayload<T> = {
  /**
   * The id of the event.
   */
  id: string
  /**
   * The name of the webhook event.
   */
  name: string
  /**
   * The data of the webhook.
   */
  data: T
  /**
   * ISO string date representing the event created at date
   */
  created_at: string
}

export type CardholderWebhookPayload = WebhookPayload<{
  cardholder_id: string
  email: string
  individual: AWXIndividual
  type: AWXCardholderType
  status: AWXCardholderStatus
}> & {
  account_id: string
}

export type ConnectedAccountWebhookPayloadFor = {
  [AWXConnectedAccountStatus.ACTION_REQUIRED]: WebhookPayload<{
    agreedToTermsAndConditionsRequired: {
      endpoint: string
      method: string
      message: string
    }
  }> & {
    account_id: string
  }
  // we don't care about this one for now. CREATED is listed in their api docs but not available as a webhook event. They do have a webhook for SUBMITTED though.
  // [AWXConnectedAccountStatus.CREATED]: {}
  [AWXConnectedAccountStatus.SUBMITTED]: WebhookPayload<{}> & {
    account_id: string
    created_at: string
  }
  [AWXConnectedAccountStatus.ACTIVE]: WebhookPayload<ConnectedAccountPayload> & {
    account_id: string
  }
  [AWXConnectedAccountStatus.SUSPENDED]: WebhookPayload<ConnectedAccountPayload> & {
    account_id: string
  }
}

/**
 * This is the type of object to expect from AWX when getting a connected account from either
 * a get request or a webhook. Treat everything as possibly undefined except for the id and status which must be always present.
 */
export type ConnectedAccountPayload = {
  id: string
  account_details?: {
    business_details?: {
      business_name: string
    }
    legal_entity_id?: string
    legal_entity_type?: string
  }
  metadata?: { [key: string]: any }
  status: AWXConnectedAccountStatus
  next_action?: {
    type: string
  }
}

export type RfiPayload = {
  id: string
}

/**
 * Represents an RFI (Request for Information) associated with an AWX connected account
 */
export type AwxConnectedAccountRfi = {
  /**
   * The id of the AWX connected account this RFI belongs to
   */
  awxConnectedAccountId: string
  /**
   * The unique identifier of the RFI
   */
  rfiId: string
  /**
   * The status of the RFI
   */
  status: RfiStatus
}

export enum RfiStatus {
  ACTION_REQUIRED = 'action_required',
  ANSWERED = 'answered',
  CLOSED = 'closed',
}

export * from './cardholder'
export * from './card'

export type AwxCardTransactionLifecycle = {
  // the lifecycle id
  id: string
  // the id of the card the transaction is associated with
  card_id: string
  // ISO 8601 timestamp of when the transaction lifecycle was created
  created_at: string
  // ISO 8601 timestamp of when the transaction lifecycle was last updated (new event received)
  updated_at: string
  // Aggregated billing totals grouped by currency. Each entry contains cumulative amounts across
  // all card transactions and transaction events within this lifecycle, including totals for
  // authorized, debited, credited, declined, expired, pending, and reversed amounts.
  billing_amounts: AwxCardTransactionLifecycleBillingAmount[]
}

export type AwxCardTransactionLifecycleBillingAmount = {
  // Three-letter ISO 4217 currency code for all amounts in this object.
  currency: string
  // Total amount authorized (funds reserved in the wallet).
  total_authorized: number
  // Total amount credited (funds returned to the wallet, e.g., refunds).
  total_credited: number
  // Total amount debited (funds moved out of the wallet after clearing).
  total_debited: number
  // Total amount declined (transaction amounts that were rejected).
  total_declined: number
  // Total amount expired (authorized amounts that were never captured and have been released).
  total_expired: number
  // Total amount still pending (outstanding authorized amount not yet cleared, reversed, or expired).
  total_pending: number
  // Total amount reversed (authorized amounts that were cancelled and released).
  total_reversed: number
}

export enum AwxWebhookEventLogSourceType {
  CARD_TRANSACTION = 'card_transaction',
  CONNECTED_ACCOUNT_TRANSFER = 'connected_account_transfer',
  TRANSFER_PAYOUT = 'transfer_payout',
  CONVERSION = 'conversion',
  CARD_DELIVERY = 'card_delivery',
  CARD_STATUS = 'card_status',
  DEPOSIT = 'deposit',
  GLOBAL_ACCOUNT = 'global_account',
  CARDHOLDER = 'cardholder',
  CONNECTED_ACCOUNT_RFI = 'connected_account_rfi',
}

export type AwxWebhookEventLog<T> = {
  id: string
  name: string
  sourceId: string
  sourceType: AwxWebhookEventLogSourceType
  connectedAccountId?: string
  createdAt: string
  receivedAt: string
  payload: T
}

export type UpsertAwxWebhookEventLogArgs = {
  id: string
  name: string
  sourceType: AwxWebhookEventLogSourceType
  sourceId: string
  connectedAccountId?: string
  createdAt: string
  receivedAt: string
  payload: unknown
}

export type ListAwxWebhookEventLogs = {
  sourceId: string
  sourceType: AwxWebhookEventLogSourceType
  sortOrder: 'asc' | 'desc'
  take: number
  skip: number
}
